/**
 * Integer-grid geometry helpers for palette drop + validation.
 */
import type {
  BoardLayout,
  LayoutBreakpoint,
  LayoutValidationIssue,
  LayoutWidget,
  WidgetSize,
  WidgetTypeDefinition,
} from './types';

export const MIN_WIDGET_W = 2;
export const MIN_WIDGET_H = 1;

export function isWidgetVisible(widget: LayoutWidget): boolean {
  return widget.hidden !== true;
}

export function visibleWidgets(widgets: readonly LayoutWidget[]): LayoutWidget[] {
  return widgets.filter(isWidgetVisible);
}

export function cloneBoardLayout(src: BoardLayout): BoardLayout {
  return {
    ...src,
    breakpoints: {
      lg: src.breakpoints.lg.map((w) => ({
        ...w,
        config: w.config ? { ...w.config } : undefined,
      })),
      md: src.breakpoints.md.map((w) => ({
        ...w,
        config: w.config ? { ...w.config } : undefined,
      })),
    },
  };
}

export function layoutFitsViewport(
  layout: BoardLayout,
  breakpoint: LayoutBreakpoint = 'lg'
): boolean {
  const cells = visibleWidgets(layout.breakpoints[breakpoint]);
  for (const cell of cells) {
    if (!Number.isInteger(cell.x) || !Number.isInteger(cell.y)) return false;
    if (!Number.isInteger(cell.w) || !Number.isInteger(cell.h)) return false;
    if (cell.w < MIN_WIDGET_W || cell.h < MIN_WIDGET_H) return false;
    if (cell.x < 0 || cell.y < 0) return false;
    if (cell.x + cell.w > layout.cols) return false;
    if (cell.y + cell.h > layout.rows) return false;
  }
  return true;
}

export function validateBoardLayout(
  layout: BoardLayout,
  lockedTypes: ReadonlySet<string>,
  titles: Readonly<Record<string, string>>,
  breakpoint: LayoutBreakpoint = 'lg',
  minSizes: Readonly<Record<string, WidgetSize | undefined>> = {}
): LayoutValidationIssue[] {
  const issues: LayoutValidationIssue[] = [];
  for (const cell of visibleWidgets(layout.breakpoints[breakpoint])) {
    const min = minSizes[cell.type];
    if (min !== undefined && (cell.w < min.w || cell.h < min.h)) {
      issues.push({
        code: 'too_small',
        type: cell.type,
        message: `${titles[cell.type] ?? cell.type} needs at least ${min.w}×${min.h} cells to stay readable on a tablet.`,
      });
    }
  }
  if (!layoutFitsViewport(layout, breakpoint)) {
    issues.push({
      code: 'overflow',
      message: 'Layout overflows the board - every card must fit the grid with no page scroll.',
    });
  }
  const cells = visibleWidgets(layout.breakpoints[breakpoint]);
  const visible = new Set(cells.map((w) => w.type));
  for (const locked of lockedTypes) {
    if (!visible.has(locked)) {
      issues.push({
        code: 'missing_locked',
        type: locked,
        message: `${titles[locked] ?? locked} cannot be removed.`,
      });
    }
  }
  // Two cards on the same cells render one under the other with no warning
  // from CSS grid; refuse to save that.
  for (let i = 0; i < cells.length; i += 1) {
    for (let j = i + 1; j < cells.length; j += 1) {
      const a = cells[i] as LayoutWidget;
      const b = cells[j] as LayoutWidget;
      if (a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h) {
        issues.push({
          code: 'overlap',
          message: `${titles[a.type] ?? a.type} overlaps ${titles[b.type] ?? b.type}.`,
        });
      }
    }
  }
  return issues;
}

export function updateWidgetInLayout(
  layout: BoardLayout,
  widgetId: string,
  patch: Partial<Pick<LayoutWidget, 'x' | 'y' | 'w' | 'h' | 'hidden' | 'config'>>
): BoardLayout {
  const mapCells = (cells: LayoutWidget[]): LayoutWidget[] =>
    cells.map((cell) => (cell.id === widgetId ? { ...cell, ...patch } : cell));
  return {
    ...layout,
    breakpoints: {
      lg: mapCells(layout.breakpoints.lg),
      md: mapCells(layout.breakpoints.md),
    },
  };
}

/**
 * Clamp origin and min size. Trailing overflow is allowed so Save can block.
 * `minSize` is the card type's own floor (`WidgetTypeDefinition.minSize`);
 * the platform floor always applies underneath it.
 */
export function clampWidgetGeometry(widget: LayoutWidget, minSize?: WidgetSize): LayoutWidget {
  const w = Math.max(MIN_WIDGET_W, minSize?.w ?? 0, Math.round(widget.w));
  const h = Math.max(MIN_WIDGET_H, minSize?.h ?? 0, Math.round(widget.h));
  const x = Math.max(0, Math.round(widget.x));
  const y = Math.max(0, Math.round(widget.y));
  return { ...widget, x, y, w, h };
}

/**
 * First free top-left cell (row by row) where a `size` card fits the
 * breakpoint's visible cards without overlap; null when the board is full.
 * Used when a card is added by a palette CLICK (no drop point).
 */
export function findFreeSpot(
  layout: BoardLayout,
  size: WidgetSize,
  breakpoint: LayoutBreakpoint = 'lg'
): { x: number; y: number } | null {
  const cells = visibleWidgets(layout.breakpoints[breakpoint]);
  for (let y = 0; y + size.h <= layout.rows; y += 1) {
    for (let x = 0; x + size.w <= layout.cols; x += 1) {
      const clash = cells.some(
        (c) => x < c.x + c.w && c.x < x + size.w && y < c.y + c.h && c.y < y + size.h
      );
      if (!clash) return { x, y };
    }
  }
  return null;
}

/** Map client point → integer grid cell origin relative to board element. */
export function clientPointToGridCell(
  boardEl: DOMRect,
  clientX: number,
  clientY: number,
  cols: number,
  rows: number,
  gapPx = 0
): { x: number; y: number } {
  const cellW = (boardEl.width - gapPx * (cols - 1)) / cols;
  const cellH = (boardEl.height - gapPx * (rows - 1)) / rows;
  if (!(cellW > 0) || !(cellH > 0)) return { x: 0, y: 0 };
  const x = Math.max(0, Math.min(cols - 1, Math.floor((clientX - boardEl.left) / cellW)));
  const y = Math.max(0, Math.min(rows - 1, Math.floor((clientY - boardEl.top) / cellH)));
  return { x, y };
}

function defaultIdForType(type: string): string {
  return type
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/_/g, '-')
    .toLowerCase();
}

function shortId(): string {
  return Math.random().toString(36).slice(2, 8).padEnd(6, '0');
}

/**
 * A fresh widget id for another instance of a multi-instance type:
 * `<kebab-type>-<shortid>`, never colliding with an id on either breakpoint.
 */
export function newWidgetId(
  layout: BoardLayout,
  type: string,
  random: () => string = shortId
): string {
  const taken = new Set(
    [...layout.breakpoints.lg, ...layout.breakpoints.md].map((widget) => widget.id)
  );
  const base = defaultIdForType(type);
  for (let attempt = 0; attempt < 50; attempt += 1) {
    const id = `${base}-${random()}`;
    if (!taken.has(id)) return id;
  }
  let n = 2;
  while (taken.has(`${base}-${n}`)) n += 1;
  return `${base}-${n}`;
}

/**
 * Place (or unhide) a widget type at integer x,y with defaultSize.
 * Single-instance: soft-hidden widgets reuse their id; new types get a kebab
 * id. Multi-instance (`def.multiInstance`): always a NEW widget, id from
 * `options.id` or `newWidgetId`.
 */
export function placeWidgetAt(
  layout: BoardLayout,
  type: string,
  x: number,
  y: number,
  def: WidgetTypeDefinition,
  staffPrototype?: LayoutWidget,
  options: { id?: string } = {}
): BoardLayout {
  const size: WidgetSize = def.defaultSize;
  const min = def.minSize;
  const existing =
    def.multiInstance === true ? undefined : layout.breakpoints.lg.find((w) => w.type === type);
  if (existing !== undefined) {
    const clamped = clampWidgetGeometry(
      {
        ...existing,
        x,
        y,
        w: existing.w || size.w,
        h: existing.h || size.h,
        hidden: false,
      },
      min
    );
    return updateWidgetInLayout(layout, existing.id, {
      x: clamped.x,
      y: clamped.y,
      w: clamped.w,
      h: clamped.h,
      hidden: false,
    });
  }

  const id =
    def.multiInstance === true
      ? (options.id ?? newWidgetId(layout, type))
      : (staffPrototype?.id ?? defaultIdForType(type));
  const base: LayoutWidget = {
    id,
    type,
    x,
    y,
    w: size.w,
    h: size.h,
    config: def.defaultConfig ? { ...def.defaultConfig } : staffPrototype?.config,
  };
  const lgCell = clampWidgetGeometry(base, min);
  const mdProto = staffPrototype
    ? { ...staffPrototype, x: lgCell.x, y: lgCell.y, w: lgCell.w, h: lgCell.h, hidden: false }
    : { ...lgCell };
  return {
    ...layout,
    breakpoints: {
      lg: [...layout.breakpoints.lg, lgCell],
      md: [...layout.breakpoints.md, mdProto],
    },
  };
}

/**
 * Hide a card. Single-instance cards are soft-hidden (geometry kept for the
 * palette to restore); a multi-instance card is REMOVED from both breakpoints.
 */
export function hideWidgetInLayout(
  layout: BoardLayout,
  widgetId: string,
  lockedTypes: ReadonlySet<string>,
  multiInstanceTypes: ReadonlySet<string> = new Set()
): BoardLayout {
  const cell = layout.breakpoints.lg.find((w) => w.id === widgetId);
  if (cell === undefined) return layout;
  if (lockedTypes.has(cell.type)) return layout;
  if (multiInstanceTypes.has(cell.type)) return removeWidgetFromLayout(layout, widgetId);
  return updateWidgetInLayout(layout, widgetId, { hidden: true });
}

export function removeWidgetFromLayout(layout: BoardLayout, widgetId: string): BoardLayout {
  return {
    ...layout,
    breakpoints: {
      lg: layout.breakpoints.lg.filter((w) => w.id !== widgetId),
      md: layout.breakpoints.md.filter((w) => w.id !== widgetId),
    },
  };
}

/** Types of a widget catalog that allow many instances. */
export function multiInstanceTypes(widgets: readonly WidgetTypeDefinition[]): Set<string> {
  return new Set(widgets.filter((w) => w.multiInstance === true).map((w) => w.type));
}

/**
 * Column count of boards saved before the grid was doubled for finer
 * placement. Every board surface (Play Entry, Coaches Highlight, Fans) moved
 * from 12 columns to {@link BOARD_GRID_COLS} with twice the rows.
 */
export const LEGACY_BOARD_GRID_COLS = 12;
export const BOARD_GRID_COLS = 24;

/**
 * One-time grid upgrade, applied on load: a board saved on the old 12-column
 * grid gets every coordinate, size, `cols` and `rows` doubled, so it renders
 * EXACTLY as before on the finer grid (each old cell = 2×2 new cells). Boards
 * already on the new grid are returned untouched (same object), so this is
 * idempotent; saving writes the new shape and the upgrade never runs again.
 */
export function upgradeLegacyGrid(layout: BoardLayout): BoardLayout {
  if (layout.cols !== LEGACY_BOARD_GRID_COLS) return layout;
  const upgraded = scaleBoardLayout(layout, 2);
  UPGRADED.add(upgraded);
  return upgraded;
}

/**
 * Boards produced by {@link upgradeLegacyGrid} in this session. A surface that
 * repairs saved boards on load (Coaches Highlight) repairs these on the grid
 * they were saved on, so the result is pixel-identical to before the upgrade.
 */
const UPGRADED = new WeakSet<BoardLayout>();

export function wasUpgradedFromLegacyGrid(layout: BoardLayout): boolean {
  return UPGRADED.has(layout);
}

/** Scale every coordinate/size and the grid itself (exact for factor 2 / ½ of an upgrade). */
export function scaleBoardLayout(layout: BoardLayout, factor: number): BoardLayout {
  const s = (n: number): number => Math.round(n * factor);
  const scale = (cells: LayoutWidget[]): LayoutWidget[] =>
    cells.map((c) => ({ ...c, x: s(c.x), y: s(c.y), w: s(c.w), h: s(c.h) }));
  return {
    ...layout,
    cols: s(layout.cols),
    rows: s(layout.rows),
    breakpoints: { lg: scale(layout.breakpoints.lg), md: scale(layout.breakpoints.md) },
  };
}

export function parseBoardLayout(raw: unknown, unit: string): BoardLayout | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const rec = raw as Record<string, unknown>;
  if (typeof rec.id !== 'string' || typeof rec.name !== 'string') return null;
  if (typeof rec.unit !== 'string' || rec.unit !== unit) return null;
  if (typeof rec.cols !== 'number' || typeof rec.rows !== 'number') return null;
  const breakpoints = rec.breakpoints as Record<string, unknown> | undefined;
  if (typeof breakpoints !== 'object' || breakpoints === null) return null;
  if (!Array.isArray(breakpoints.lg) || !Array.isArray(breakpoints.md)) return null;
  const parseCells = (cells: unknown[]): LayoutWidget[] | null => {
    const out: LayoutWidget[] = [];
    for (const cell of cells) {
      if (typeof cell !== 'object' || cell === null) return null;
      const c = cell as Record<string, unknown>;
      if (typeof c.id !== 'string' || typeof c.type !== 'string') return null;
      if (typeof c.x !== 'number' || typeof c.y !== 'number') return null;
      if (typeof c.w !== 'number' || typeof c.h !== 'number') return null;
      out.push({
        id: c.id,
        type: c.type,
        x: c.x,
        y: c.y,
        w: c.w,
        h: c.h,
        hidden: c.hidden === true ? true : undefined,
        config:
          typeof c.config === 'object' && c.config !== null
            ? (c.config as Record<string, unknown>)
            : undefined,
      });
    }
    return out;
  };
  const lg = parseCells(breakpoints.lg);
  const md = parseCells(breakpoints.md);
  if (lg === null || md === null) return null;
  return upgradeLegacyGrid({
    id: rec.id,
    name: rec.name,
    unit,
    cols: rec.cols,
    rows: rec.rows,
    breakpoints: { lg, md },
  });
}
